import { createSelector } from 'reselect';
import setInPlace from 'lodash/set';

import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import { ActionMap } from 'ia-react-core/~/interfaces/ReducerActions';
import { SelectorMap } from 'ia-react-core/~/interfaces/ReducerSelectors';

import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import getScoped from './getScoped';

// Export to allow reset during tests
export const ids = {
  actionId: 0,
  selectorId: 0,
};

interface RegisterVirtualsOptions<State> {
  actions: ActionMap<any>;
  selectors: SelectorMap;
  store: AugmentedStore<State>;
  scope: string[];
  path: string;
}

export default function registerVirtuals<State>({ actions, selectors, store, scope, path }: RegisterVirtualsOptions<State>): [Record<string, string>, () => void] {
  const virtuals = {};

  const actionsKeys = getActionsKeys<State>(actions, { store });
  const selectorsKeys = getSelectorsKeys<State>(selectors, { store, path, scope });

  if (actionsKeys) {
    setInPlace(virtuals, ['[actions]'], actionsKeys);
  }

  if (selectorsKeys) {
    setInPlace(virtuals, ['[selectors]'], selectorsKeys);
  }

  return [virtuals, () => {
    if (actionsKeys) {
      Object.values(actionsKeys).forEach((actionKey) => {
        delete store.actionStore[actionKey];
      });
    }

    if (selectorsKeys) {
      Object.values(selectorsKeys).forEach((selectorKey) => {
        delete store.selectorStore[selectorKey];
      });
    }
  }];
}

function getActionsKeys<State>(actions: ActionMap, { store }: { store: AugmentedStore<State>}) {
  if (!actions) {
    return null;
  }

  const actionsKeys = Object.keys(actions).reduce<Record<string, string>>((result, actionName: StringKeyOf<typeof actions>) => {
    ids.actionId += 1;
    const actionKey = `${actionName}-${ids.actionId}`;
    store.actionStore[actionKey] = (...args: unknown[]) => store.dispatch(actions[actionName](...args));
    const formattedActionName = actionName.substring(0, 1).toLowerCase() + actionName.substring(1);
    result[formattedActionName] = actionKey;
    return result;
  }, {});

  return actionsKeys;
}

function getSelectorsKeys<State>(selectors: SelectorMap, { store, path, scope }: { store: AugmentedStore<State>; path: string; scope: string[]}) {
  if (!selectors) {
    return null;
  }

  const scopedStateSelector = (state: unknown) => getScoped(state, path, scope);

  const selectorsKeys = Object.keys(selectors).reduce<Record<string, string>>((result, selectorName) => {
    ids.selectorId += 1;
    const selectorKey = `${selectorName}-${ids.selectorId}`;
    const selector = selectors[selectorName]();
    store.selectorStore[selectorKey] = createSelector(
      scopedStateSelector,
      (state: unknown) => state,
      () => scope,
      selector,
    );
    const formattedSelectorName = selectorName.substring(0, 1).toLowerCase() + selectorName.substring(1);
    result[formattedSelectorName] = selectorKey;
    return result;
  }, {});

  return selectorsKeys;
}
